import appConfig from "../config.json";
const { cdn, brands } = appConfig;
const productionEnvironment = "prod";
const latestEnvironment = "latest";
const developmentEnvironment = "development";
const localEnvironment = "localhost";
const hostnamesAllowed = [
	localEnvironment,
	latestEnvironment,
	"stage",
	"lt01",
	"lt",
	"load",
	productionEnvironment,
];
const envMapping = { load: "lt01", lt: "lt01" };
const syndicatedPathname = "/media/layout/syndicated/";
const helpUiPathname = "/media/layout/help-ui/";
const developmentSubdomains = [
	localEnvironment,
	"versions",
	"pep-local",
	"dev",
	"local",
	"dp",
	"dvc-local",
	"dpj",
];
const localeDetectionRegExp = /^\/[\w]{2}(-|_)[\w]{2}\/|^\/(\w{2})\//;
const detectDateRegExp = /^\d{4}-\d{2}-\d{2}$/;
const overflowHiddenClass = "syndicated-overflow-hidden";

export const isCdnPage = () => {
	return (
		window?.location.pathname.includes(syndicatedPathname) ||
		window?.location.pathname.includes(helpUiPathname)
	);
};

export const objectIsEmpty = (object) => !Object.keys(object || {}).length;

export const arrayIsEmpty = (array = []) => array.length === 0;

export const mapString = (target = "", ...args) => {
	let index = -1;
	return target.replace(/{[a-zA-Z0-9._-]+}/gi, () => {
		index++;
		return args[index];
	});
};

export const mapStringWithObject = (target, obj) => {
	return target.replace(/{[a-zA-Z0-9._-]+}/gi, (...args) => {
		const [matched] = args;
		const replaced = matched.replace(/{|}/g, "");
		return obj[replaced];
	});
};
export const highlightNotMatches = (text, term) => {
	const regex = new RegExp(`(${term})`, "gi");
	const parts = text.split(regex);
	const results = parts
		.map((part) => {
			if (part.toLowerCase() === term.toLowerCase()) {
				return part;
			} else if (part.trim() === "") {
				return part;
			} else {
				return `<em>${part}</em>`;
			}
		})
		.join("");

	return results;
};

export const highlightMatches = (text, search) => {
	if (search === "") {
		return text;
	}
	const regex = new RegExp(`(${search})`, "gi");
	return text.replace(regex, "<b>$1</b>");
};

export const getEnvironment = () => {
	const { hostname, pathname } = window?.location;
	let mainSubdomain = hostname.split(".")[0];

	if (isCdnPage()) {
		mainSubdomain = pathname.split("/")[4];
	}

	if (developmentSubdomains.includes(mainSubdomain))
		return developmentEnvironment;
	else if (hostnamesAllowed.includes(mainSubdomain))
		return envMapping[mainSubdomain] || mainSubdomain;
	else return productionEnvironment;
};

export const elementIsDisplayed = (element) =>
	!!(element?.offsetHeight && element?.offsetWidth);

export const isDevelopmentEnvironment = (
	environment = process.env.NODE_ENV
) => {
	return developmentEnvironment === environment;
};

export const isIsolatedEnvironment = () => {
	return (
		[developmentEnvironment, "test"].includes(process.env.NODE_ENV) ||
		isCdnPage()
	);
};

export const getCdn = (isAssetsCdn = false) => {
	const baseUrl = cdn.baseUrl;
	const assetsCdn = `${baseUrl}${cdn.assets}`;
	const syndicatedCdn = `${baseUrl}${cdn.syndicated}`;
	let env = getEnvironment();
	if (env === developmentEnvironment) env = latestEnvironment;
	return !isAssetsCdn && isDevelopmentEnvironment()
		? "/assets"
		: mapString(
				isAssetsCdn ? assetsCdn : syndicatedCdn,
				env === productionEnvironment ? "" : `stage.`,
				env
		  );
};

export const addCurrentRedirectUrl = (
	pathname,
	validateAppRedirect,
	validatePathname,
	brand,
	locale,
	absoluteLinksFromBrand
) => {
	let targetUrl = window?.location.href.replace(window?.location.origin, "");
	if (absoluteLinksFromBrand && brands?.[brand]?.domains) {
		targetUrl =
			brands?.[brand]?.domains[locale] ||
			brands?.[brand]?.domains.default ||
			"";
	}
	targetUrl = encodeURIComponent(targetUrl);
	if (validateAppRedirect && !pathname.includes("appRedirect")) {
		return `${pathname}?appRedirect=${targetUrl}&cancelUrl=${targetUrl}`;
	} else if (validatePathname && !pathname.includes(targetUrl)) {
		return `${pathname}${targetUrl.replace("%2F", "")}`;
	} else {
		return mapString(pathname, targetUrl);
	}
};

export const isTravelAgent = (userType) => {
	return (
		userType &&
		!["GUEST", "GUEST_B2B2C", "TRAVEL_AGENT_NOT_SIGNED_IN"].includes(userType)
	);
};

export const isCurrentPathPresent = (listOfPaths = [], updatedLocation) => {
	const { pathname } = window?.location;
	let currentPath = pathname;
	if (updatedLocation && pathname !== updatedLocation) {
		currentPath = updatedLocation;
	}
	const localeMatches = pathname.match(localeDetectionRegExp);
	const path = localeMatches
		? `/${currentPath}`.replace(localeMatches[0], "")
		: currentPath;
	const pathWithoutSlash = path.replace(/\/$/, "");
	const pathWithSlash = path.endsWith("/") ? path : `${path}/`;

	return listOfPaths?.some(
		(rule) =>
			pathWithoutSlash.match(`^${rule.replace(/\*/g, ".*")}$`) ||
			pathWithSlash.match(`^${rule.replace(/\*/g, ".*")}$`)
	);
};

export const decodeJsonWebToken = (token) => {
	if (token) {
		try {
			const base64 = token
				.split(".")?.[1]
				?.replace(/-/g, "+")
				.replace(/_/g, "/");

			const decodedPayload = decodeURIComponent(
				window
					.atob(base64)
					.split("")
					.map((c) => {
						return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join("")
			);
			return JSON.parse(decodedPayload);
		} catch (error) {
			console.error(error);
		}
	}
	return null;
};

export const validateJsonWebToken = (token) => {
	try {
		const tokenPayload = decodeJsonWebToken(token);
		if (
			tokenPayload &&
			tokenPayload.exp &&
			!isNaN(tokenPayload.exp) &&
			tokenPayload.exp * 1000 > new Date().getTime() + 120000
		) {
			return tokenPayload;
		}
	} catch (error) {
		console.error(error);
	}
	return false;
};

export const isAllMarketingSubscribed = (
	marketingArray = [],
	marketingCodes = []
) => {
	let isSubscribed = true;
	for (let i = 0; i < marketingArray.length; i++) {
		if (
			marketingCodes.includes(marketingArray[i].code) &&
			!marketingArray[i].subscribed
		) {
			isSubscribed = false;
			break;
		}
	}
	return isSubscribed;
};

export const triggerEventListener = (elementId, eventHandler, add = true) => {
	const element = document?.getElementById(elementId);
	element?.removeEventListener("click", eventHandler);
	if (add) element?.addEventListener("click", eventHandler);
};

export const removeHTMLTags = (searchQuery = "") => {
	return searchQuery.replace(/<[^>]*>/g, "");
};

export const toggleBodyClass = (add = true, customClass = "") => {
	document?.body.classList?.toggle(customClass || overflowHiddenClass, add);
};

export const formatDatesToMMDDYYYY = (data) => {
	const formattedData = { ...data };

	for (const key in formattedData) {
		const value = formattedData[key];
		if (detectDateRegExp.test(value)) {
			const [year, month, day] = value.split("-");
			formattedData[key] = `${month}/${day}/${year}`;
		}
	}

	return formattedData;
};

export const focusChatModal = (
	event,
	chatModal,
	removeListener,
	previousElement
) => {
	if (
		event.altKey &&
		event.code === "KeyC" &&
		!["INPUT", "TEXTAREA"].includes(event.target.tagName)
	) {
		if (chatModal) {
			if (previousElement) previousElement.reference = document?.activeElement;
			chatModal.focus();
		} else {
			removeListener();
			return;
		}
	} else {
		return;
	}
};

export const getLocaleInDefaultFormat = (currentLocale) => {
	if (currentLocale) {
		const currentLocaleParts = currentLocale.replace("-", "_").split("_");
		return `${currentLocaleParts[0].toLowerCase()}${
			currentLocaleParts[1] ? `_${currentLocaleParts[1].toUpperCase()}` : ""
		}`;
	} else {
		return "en_US";
	}
};

export const removeHoverOfSection = () => {
	const openSection = document?.querySelector(".syndicated-section--onhover");
	if (openSection) {
		openSection.classList.remove("syndicated-section--onhover");
	}
};

export const extractIcon = (iconSource) => {
	return iconSource.split('<span class="icon__')[1].split(" ")[0];
};

export const buildModifierClass = (
	condition,
	mainClass,
	modifier
) => {
	return (component,) => {
		return condition ? `${mainClass}${component}${modifier}` : "";
	};
};
